import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import TimeFilter from "./TimeFilter";
import { ChargerFillIcon } from "../../../Common/Svgs/ChargerIcon";
import SolarIcon from "../../../Common/Svgs/SolarIcon";
import { BatteryChargingIcon } from "../../../Common/Svgs/BatteryChargeIcon";
import InfoCircleIcon from "../../../Common/Svgs/InfoCircleIcon";
import CustomTooltip from "../../../Core/Components/CustomTooltip";
import TripsIcon from "../../../Common/Svgs/TripsIcon";
import SessionIcon from "../../../Common/Svgs/SessionIcon";
import PowerGridIcon from "../../../Common/Svgs/PowerGridIcon";
import getFormatedFloat from "../../../Utils/getFormatedFloat";
import usePermission from "../../../Core/hooks/usePermission";
import { PERMISSIONS } from "../../Login/constants";

export const SolarPanelKPI = ({ timeFilter, setTimeFilter }) => {
  const { hubDetailsKpi } = useSelector((state) => state.hubs);
  const { locationDetails } = useSelector((state) => state.loggedInUser);

  const getSunsetTime = useCallback(() => {
    if (!locationDetails?.sunset) return "--";

    const sunsetMs = locationDetails.sunset * 1000;

    return new Date(sunsetMs).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [locationDetails]);

  return (
    <div className="flex-1 flex flex-col rounded-md border border-light-grey h-[340px]">
      <div className="flex-1 bg-background-white px-6 pt-6 rounded-t-md">
        <div className="flex items-start justify-between">
          <div>
            <TimeFilter filter={timeFilter} setFilter={setTimeFilter} />
            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Last Synced: 2 Mins Ago
            </p>
          </div>

          <div>
            <div className="flex items-center gap-x-2">
              <img src="/assets/icons/hubs/cloud-sun.svg" alt="temperature" />

              <p className="font-poppins font-light text-xs text-dark-gray">
                <span className="font-medium text-base">
                  {parseInt(Math.floor(locationDetails?.temperature), 10)}
                </span>{" "}
                °C
              </p>
            </div>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Sunset At {getSunsetTime()}
            </p>
          </div>
        </div>

        <div className="mt-8 flex items-start gap-x-8 relative">
          <div className="">
            <p className="font-poppins text-2xl font-[200] text-dark-gray">
              <span className="font-semibold text-black">
                {hubDetailsKpi?.totalEnergyProduced || "--"}
              </span>{" "}
              kWh
            </p>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Total Energy Produced
            </p>

            {/* indicator line overlapping with solar panel image */}
            <div className="absolute w-[133px] left-[76px] top-[51%]">
              <img
                src="/assets/images/hubs/kpi-indicator.png"
                alt="indicator"
              />
            </div>

            <div className="mt-[26px] bg-green-10 flex items-center gap-x-2 py-1 px-2 max-w-max rounded-md">
              <p className="font-poppins font-medium text-xs text-green">
                Active
              </p>

              <ChargerFillIcon className="text-green" />
            </div>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Since: 06:00 AM
            </p>
          </div>

          <div className="flex-1 h-[174px]">
            <img
              src="/assets/images/hubs/solar-panel.png"
              className="w-full h-full"
              alt="solar-panel"
            />
          </div>
        </div>
      </div>

      <div className="h-[52px] py-4 px-6 flex items-center justify-between bg-[#F1F1F1] rounded-b-md">
        <div className="flex items-center gap-x-1">
          <img src="/assets/icons/hubs/impact.svg" alt="impact" />
          <p className="font-poppins font-light text-xs text-dark-gray">
            Impact:
          </p>
        </div>

        <p className="font-poppins font-light text-xs text-dark-gray">
          <span className="font-medium text-black">--%</span> Self powered today
        </p>
      </div>
    </div>
  );
};

export const EnergyStorageKPI = () => {
  const navigate = useNavigate();

  const { hubDetailsKpi } = useSelector((state) => state.hubs);

  const hasPermission = usePermission([
    PERMISSIONS.ENERGY_MANAGEMENT_MODIFY_STORAGE,
  ]);

  const getCombinedStorageValue = () => {
    const total =
      parseFloat(hubDetailsKpi?.totalEnergyStoredInSolar || 0) +
      parseFloat(hubDetailsKpi?.totalEnergyStoredInGrid || 0);

    return total > 0 ? getFormatedFloat(total, 0) : "--";
  };

  return (
    <div className="flex-1 flex flex-col rounded-md border border-light-grey h-[340px]">
      <div className="flex-1 bg-background-white px-6 pt-6 pb-6 rounded-t-md">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-x-2">
              <img
                src="/assets/icons/hubs/energy-storage.svg"
                className="w-5 h-5"
                alt="energy-storage"
              />

              <p className="font-poppins font-medium text-base">
                Energy Storage
              </p>
            </div>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Last Synced: 2 Mins Ago
            </p>
          </div>

          {hasPermission && (
            <button
              type="button"
              className="flex items-center gap-x-2 py-[11px] px-4 rounded-md bg-primary hover:bg-primary-50"
              onClick={() => navigate("/storage-control-panel")}
            >
              <img
                src="/assets/icons/hubs/manage.svg"
                className="w-5 h-5"
                alt="manage"
              />

              <p className="font-poppins font-medium text-xs text-background-white">
                Manage
              </p>
            </button>
          )}
        </div>

        <p className="mt-8 font-poppins text-2xl font-[200] text-dark-gray">
          <span className="font-semibold text-black">
            {getCombinedStorageValue()}
          </span>{" "}
          kWh
        </p>

        <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
          Total Energy Stored
        </p>

        <div className="mt-[27px] w-full">
          <div className="flex items-center justify-between">
            <p className="font-poppins font-light text-xs text-dark-gray">
              100%
            </p>
            <p className="font-poppins font-light text-xs text-dark-gray">0%</p>
          </div>

          <div className="mt-[3px] flex items-center gap-x-1">
            <div
              className="h-2 rounded-full bg-yellow"
              style={{ width: "100%" }}
            />
            <div
              className="h-2 rounded-full bg-green"
              style={{ width: "2%" }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <SolarIcon />
              <p className="font-aileron font-normal text-xs text-dark-gray">
                Solar
              </p>
            </div>

            <p className="font-poppins font-medium text-xs">
              {hubDetailsKpi?.totalEnergyStoredInSolar
                ? getFormatedFloat(hubDetailsKpi.totalEnergyStoredInSolar, 0)
                : "--"}{" "}
              kWh
            </p>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <PowerGridIcon />
              <p className="font-aileron font-normal text-xs text-dark-gray">
                Grid
              </p>
            </div>

            <p className="font-poppins font-medium text-xs">-- kWh</p>
          </div>
        </div>
      </div>

      <div className="h-[52px] py-4 px-6 flex items-center justify-between bg-[#F1F1F1] rounded-b-md">
        <div className="flex items-center gap-x-1">
          <img src="/assets/icons/hubs/impact.svg" alt="impact" />
          <p className="font-poppins font-light text-xs text-dark-gray">
            Impact:
          </p>
        </div>

        <p className="font-poppins font-light text-xs text-dark-gray">
          <span className="font-medium text-black">--%</span> of storage
          utilized
        </p>
      </div>
    </div>
  );
};

export const EnergyPredictionKPI = () => {
  const { energyPrediction } = useSelector((state) => state.hubs);

  const getEnergyNeeded = () => {
    const total =
      parseFloat(energyPrediction?.trip?.energy || 0) +
      parseFloat(energyPrediction?.session?.energy || 0);

    if (Number.isNaN(total)) return "--";

    return parseFloat(total).toFixed(0);
  };

  const getBatteriesNeeded = () => {
    const total =
      parseFloat(energyPrediction?.trip?.batteries || 0) +
      parseFloat(energyPrediction?.session?.batteries || 0);

    if (Number.isNaN(total)) return "--";

    return parseFloat(total).toFixed(0);
  };

  const getStyle = () => {
    const availableEnergy = parseFloat(
      energyPrediction?.battery?.capacity || 0,
    );

    const requiredEnergy =
      parseFloat(energyPrediction?.trip?.energy || 0) +
      parseFloat(energyPrediction?.session?.energy || 0);

    if (requiredEnergy > availableEnergy) return "text-alert-red";
    return "text-green";
  };

  return (
    <div className="flex-1 rounded-md border border-light-grey h-[340px]">
      <div className="bg-background-white px-6 pt-6 pb-6 rounded-t-md">
        <div className="flex items-center gap-x-2">
          <BatteryChargingIcon size={20} />

          <p className="font-poppins font-medium text-base">
            Energy Prediction
          </p>

          <CustomTooltip tooltipTitle="Estimated energy required for upcoming trips and sessions based on the current schedules. Helps you plan battery usage and ensure sufficient reserves from solar, storage, or grid sources.">
            <div className="cursor-pointer">
              <InfoCircleIcon size={20} className="text-dark-gray" />
            </div>
          </CustomTooltip>
        </div>

        <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
          Last Synced: 2 Mins Ago
        </p>

        <div className="mt-8 flex items-center justify-between">
          <div>
            <p className="font-poppins text-2xl font-[200] text-dark-gray">
              <span className="font-semibold text-black">
                {getEnergyNeeded()}
              </span>{" "}
              kWh
            </p>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Energy Needed
            </p>
          </div>

          <div>
            <p className={`font-poppins text-2xl font-semibold ${getStyle()}`}>
              {getBatteriesNeeded()}
            </p>

            <p className="mt-2 font-aileron text-xs font-normal text-dark-gray capitalize">
              Batteries Needed
            </p>
          </div>
        </div>

        <div className="mt-[27px] w-full">
          <div className="flex items-center justify-between">
            <p className="font-poppins font-light text-xs text-dark-gray">
              Energy Available
            </p>
            <p className="font-poppins font-light text-xs text-dark-gray">
              {energyPrediction?.battery?.capacity || "--"} kWh
            </p>
          </div>

          <div className="mt-[3px] relative">
            <div
              className="h-2 absolute rounded-full bg-yellow"
              style={{ width: "0%" }}
            />
            <div
              className="h-2 rounded-full bg-light-grey"
              style={{ width: "100%" }}
            />
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <TripsIcon size={16} className="text-green" />
              <p className="font-aileron font-normal text-xs text-dark-gray">
                Upcoming Trips
              </p>
            </div>

            <p className="font-poppins font-medium text-xs">
              {energyPrediction?.trip?.count ?? "--"}
            </p>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <SessionIcon size={16} className="text-green" />
              <p className="font-aileron font-normal text-xs text-dark-gray">
                Upcoming Sessions
              </p>
            </div>

            <p className="font-poppins font-medium text-xs">
              {energyPrediction?.session?.count ?? "--"}
            </p>
          </div>
        </div>
      </div>

      <div className="h-[52px] py-4 px-6 flex items-center justify-between bg-[#F1F1F1] rounded-b-md">
        <div className="flex items-center gap-x-1">
          <img src="/assets/icons/hubs/impact.svg" alt="impact" />
          <p className="font-poppins font-light text-xs text-dark-gray">
            Impact:
          </p>
        </div>

        <p className="font-poppins font-light text-xs text-dark-gray">
          <span className="font-medium text-black">--%</span> of predicted
          demand achieved
        </p>
      </div>
    </div>
  );
};
